import { useTranslation } from 'react-i18next';

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import TooltipComponent from '../common/Tooltip/Tooltip';
import API from '../../utils/ApiConfig';
import { GET_PERIOD_INFO, GET_TIME_DIMENSION } from '../../utils/ApiEndpoints';
import { FooterButtonGroup } from './FooterButtonGroup';
import { WorkflowPopup } from './WorkflowPopup';
import { MuiDialog } from '../common/Mui/Dialog/MuiDialog';
import { CopyVersionDropdownGroup } from './CopyVersionDropdownGroup';
import SuccessMessage from '../common/SuccessMessage/SuccessMessage';

export const CopyVersion = props => {
  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`copyversion:${e}`, data) : t(`copyversion:${e}`));
  const [showDialog, setShowDialog] = useState(false);
  const [showBBDialog, setShowBBDialog] = useState(false);
  const [showPDDialog, setShowPDDialog] = useState(false);
  const [copyOption, setCopyOption] = useState('');
  const [isDatesSelected, setIsDatesSelected] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [isBBDestinationDataApplied, setIsBBDestinationDataApplied] = useState(false);
  const [isPDDestinationDataApplied, setIsPDDestinationDataApplied] = useState(false);

  const [timeDimensionInfo, setTimeDimensionInfo] = useState({});
  const [periodMap, setPeriodMap] = useState(null);

  const [sourceStartDate, setSourceStartDate] = useState('');
  const [sourceEndDate, setSourceEndDate] = useState('');
  const [destStartDate, setDestStartDate] = useState('');
  const [sourceBeginningBalanceDate, setSourceBeginningBalanceDate] = useState('');

  const [sourceStartPeriod, setSourceStartPeriod] = useState(null);
  const [sourceStartYear, setSourceStartYear] = useState({ id: new Date().getFullYear() - 1, name: new Date().getFullYear() - 1 });
  const [sourceStartPeriods, setSourceStartPeriods] = useState([]);
  const [sourceStartYears, setSourceStartYears] = useState([]);

  const [sourceEndPeriod, setSourceEndPeriod] = useState(null);
  const [sourceEndYear, setSourceEndYear] = useState(null);
  const [sourceEndPeriods, setSourceEndPeriods] = useState([]);
  const [sourceEndYears, setSourceEndYears] = useState([]);

  const [destinationStartPeriod, setDestinationStartPeriod] = useState({});
  const [destinationStartYear, setDestinationStartYear] = useState({ id: new Date().getFullYear(), name: new Date().getFullYear() });

  const [destinationStartPeriods, setDestinationStartPeriods] = useState([]);
  const [destinationStartYears, setDestinationStartYears] = useState([]);

  const [sourceBeginningBalancePeriod, setSourceBeginningBalancePeriod] = useState({});
  const [sourceBeginningBalanceYear, setSourceBeginningBalanceYear] = useState({
    id: new Date().getFullYear() - 1,
    name: new Date().getFullYear() - 1,
  });
  const [sourceBeginningBalancePeriods, setSourceBeginningBalancePeriods] = useState([]);
  const [sourceBeginningBalanceYears, setSourceBeginningBalanceYears] = useState([]);

  const NEVER_ENDS = tc('NEVER_ENDS');
  const CLOSING_ENTRY = tc('CLOSING_ENTRY');
  const BEGINNING_BALANCE = tc('BEGINNING_BALANCE');

  const copyVersionService = props.pageParams.services.copyVersionService;

  const getTimeDimension = planId => API.get(GET_TIME_DIMENSION(planId));
  const getPeriodInfo = (
    planId,
    sourceStartPeriod,
    sourceStartYear,
    destinationStartPeriod,
    destinationStartYear,
    sourceEndPeriod,
    sourceEndYear,
    sourceBeginningBalancePeriod,
    sourceBeginningBalanceYear
  ) =>
    API.post(GET_PERIOD_INFO, {
      planId: planId,
      sourceStartPeriod: sourceStartPeriod,
      sourceStartYear: sourceStartYear,
      destStartPeriod: destinationStartPeriod,
      destStartYear: destinationStartYear,
      sourceEndPeriod: sourceEndPeriod,
      sourceEndYear: sourceEndYear,
      sourceBeginningBalancePeriod: sourceBeginningBalancePeriod,
      sourceBeginningBalanceYear: sourceBeginningBalanceYear,
    });

  useEffect(() => {
    if (showDialog) {
      getTimeDimension(copyVersionService.planID).then(response => {
        if (response.data) {
          setTimeDimensionInfo(response.data);
        }
        if (response.data.version && parseInt(response.data.version)) {
          setPeriodMap({
            BB: '0',
            P01: '1',
            P02: '2',
            P03: '3',
            P04: '4',
            P05: '5',
            P06: '6',
            P07: '7',
            P08: '8',
            P09: '9',
            P10: '10',
            P11: '11',
            P12: '12',
            C: '999',
            NE: 'NE',
          });
        }
        if (response.data.periodsInYear === 13) {
          setPeriodMap({
            BB: '0',
            P01: '1',
            P02: '2',
            P03: '3',
            P04: '4',
            P05: '5',
            P06: '6',
            P07: '7',
            P08: '8',
            P09: '9',
            P10: '10',
            P11: '11',
            P12: '12',
            P13: '13',
            C: '999',
            NE: 'NE',
          });
        }
      });
    }
  }, [showDialog]);

  useEffect(() => {
    updateSourceAndDestinationStartEndYears();
  }, [timeDimensionInfo.firstFiscalYear, timeDimensionInfo.numOfYears]);

  useEffect(() => {
    if (showBBDialog || showPDDialog) {
      if (copyOption === 'BB') {
        initSourceBeginningBalance();
        calculateYearAndPeriodInfo(true);
      } else {
        initPeriods();
        calculateYearAndPeriodInfo(true);
      }
    }
  }, [showBBDialog, showPDDialog]);

  useEffect(() => {
    if (copyOption) {
      calculatePeriods();
      getInitialPeriods();
    }
  }, [periodMap, copyOption]);

  useEffect(() => {
    getPeriodsInfoAndUpdateDateRange();
  }, [sourceStartPeriod, sourceEndPeriod, destinationStartPeriod, destinationStartYear]);

  useEffect(() => {
    calculatePeriods();
    getPeriodInfo(
      copyVersionService.planID,
      sourceStartPeriod ? sourceStartPeriod.name : null,
      sourceStartYear ? sourceStartYear.name : null,
      destinationStartPeriod.name,
      destinationStartYear.name,
      sourceEndPeriod ? sourceEndPeriod.name : null,
      sourceEndYear ? sourceEndYear.name : null,
      sourceBeginningBalancePeriod ? sourceBeginningBalancePeriod.name : null,
      sourceBeginningBalanceYear ? sourceBeginningBalanceYear.name : null
    ).then(response => {
      getStartAndEndPeriods(response.data);
    });
  }, [sourceBeginningBalancePeriod, sourceBeginningBalanceYear]);

  useEffect(() => {
    if (sourceEndPeriod && sourceEndPeriod.name === NEVER_ENDS) {
      setSourceEndYear({ id: '', name: '' });
      calculatePeriods();
    } else {
      let tempSourceEndYear;
      if (!sourceEndYear || sourceEndYear.id === '') {
        tempSourceEndYear = { id: new Date().getFullYear(), name: new Date().getFullYear() };
        setSourceEndYear({ id: new Date().getFullYear(), name: new Date().getFullYear() });
      }
      let year;
      if (tempSourceEndYear) {
        year = sourceEndYears.find(el => el.name === tempSourceEndYear.name);
      } else {
        year = sourceEndYears.find(el => el.name === sourceEndYear.name);
      }
      if (!year) {
        setSourceEndYear(sourceEndYears[0]);
      }

      calculatePeriods();
      if (sourceStartYear && sourceEndYear) {
        if (sourceStartYear.name === sourceEndYear.name) {
          let endPeriod = parseInt(sourceEndPeriod.id);
          sourceStartPeriods.forEach((item, key) => {
            let startPeriod = parseInt(item.id);
            if (startPeriod === endPeriod) {
              sourceStartPeriods.splice(key + 1, sourceStartPeriods.length - (key + 1));
              let srcStartPeriod = sourceStartPeriods.filter(el => el.name === sourceStartPeriod.name);
              if (srcStartPeriod.length > 0) {
                setSourceStartPeriod({ id: srcStartPeriod[0].id, name: srcStartPeriod[0].name });
              } else {
                setSourceStartPeriod(sourceStartPeriods[0]);
              }
            }
          });
        }
      }
    }
  }, [sourceEndPeriod]);

  useEffect(() => {
    calculatePeriods();
    if (
      sourceStartYear &&
      sourceEndYear &&
      sourceStartYear.name === sourceEndYear.name &&
      parseInt(sourceEndPeriod.id) < parseInt(sourceStartPeriod.id)
    ) {
      let startPeriod = parseInt(sourceStartPeriod.id);

      sourceEndPeriods.forEach((item, key) => {
        let endPeriod = parseInt(item.id);
        if (endPeriod === startPeriod) {
          sourceEndPeriods.splice(0, key);
          let srcEndPeriod = sourceEndPeriods.filter(el => el.name === sourceEndPeriod.name);
          if (srcEndPeriod.length > 0) {
            setSourceEndPeriod({ id: srcEndPeriod[0].id, name: srcEndPeriod[0].name });
          } else {
            setSourceEndPeriod(sourceEndPeriods[0]);
          }
        }
      });
    }
  }, [sourceStartPeriod]);

  useEffect(() => {
    if (sourceStartYear && sourceEndYear) {
      let startYear = parseInt(sourceStartYear.name.toString());
      let endYear;
      if (sourceEndYear.name) {
        endYear = parseInt(sourceEndYear.name.toString());
      } else {
        endYear = parseInt('9999');
      }

      setSourceEndYears([]);
      let timeFiscalStartYear = parseInt(timeDimensionInfo.firstFiscalYear);
      let timeFiscalEndYear = parseInt(timeDimensionInfo.firstFiscalYear) + parseInt(timeDimensionInfo.numOfYears) - 1;

      let tempSourceEndYears = [];
      for (let t = timeFiscalStartYear; t <= timeFiscalEndYear; t++) {
        tempSourceEndYears.push({ id: t, name: t });
      }
      setSourceEndYears(tempSourceEndYears);

      if (endYear < startYear) {
        tempSourceEndYears.forEach((item, key) => {
          let year = parseInt(item.name.toString());
          if (year === startYear) {
            tempSourceEndYears.splice(0, key);
            let srcEndYear = tempSourceEndYears.filter(el => el.name === sourceEndYear.name);

            if (srcEndYear.length > 0) {
              setSourceEndYear({ id: srcEndYear[0].id, name: srcEndYear[0].name });
            } else {
              setSourceEndYear(tempSourceEndYears[0]);
            }
          }
        });
      }
      getPeriodsInfoAndUpdateDateRange();
    }
  }, [sourceStartYear]);

  useEffect(() => {
    if (sourceStartYear && sourceEndYear) {
      let startYear = parseInt(sourceStartYear.name.toString());
      let endYear = parseInt(sourceEndYear.name);

      setSourceStartYears([]);

      let timeFiscalStartYear = parseInt(timeDimensionInfo.firstFiscalYear);
      let timeFiscalEndYear = parseInt(timeDimensionInfo.firstFiscalYear) + parseInt(timeDimensionInfo.numOfYears) - 1;

      let tempSourceStartYears = [];
      for (let t = timeFiscalStartYear; t <= timeFiscalEndYear; t++) {
        tempSourceStartYears.push({ id: t, name: t });
      }
      setSourceStartYears(tempSourceStartYears);

      //this flag is to prevent multiple getPeriodInfo API calls and its subsequent date range calculation when
      //end year is set to be less than start year. Even though useEffects seemed to be executing in the correct order
      //the API calls they were triggering were sometimes 'stacking up' weirdly causing date range not to update correctly
      let endYearLessThanStartAndTriggeredByEndYearChange = false;

      if (endYear < startYear) {
        endYearLessThanStartAndTriggeredByEndYearChange = true;
        tempSourceStartYears.forEach((item, key) => {
          let year = parseInt(item.name.toString());
          if (year === endYear) {
            tempSourceStartYears.splice(key + 1, tempSourceStartYears.length - (key + 1));
            let srcStartYear = tempSourceStartYears.filter(el => el.name === sourceStartYear.name);

            if (srcStartYear.length > 0) {
              setSourceStartYear({ id: srcStartYear[0].id, name: srcStartYear[0].name });
            } else {
              setSourceStartYear(tempSourceStartYears[0]);
            }
          }
        });
      }
      if (!endYearLessThanStartAndTriggeredByEndYearChange) {
        getPeriodsInfoAndUpdateDateRange();
      }
    }
  }, [sourceEndYear]);

  const sourceEndYearDisabled = useMemo(() => !!sourceEndPeriod && sourceEndPeriod.name === NEVER_ENDS, [sourceEndPeriod]);

  const getPeriodsInfoAndUpdateDateRange = () => {
    if (!copyVersionService.datesSelected) {
      if (sourceStartPeriod && sourceStartYear && destinationStartPeriod && destinationStartYear && sourceEndPeriod && sourceEndYear) {
        getPeriodInfo(
          copyVersionService.planID,
          sourceStartPeriod.name,
          sourceStartYear.name,
          destinationStartPeriod.name,
          destinationStartYear.name,
          sourceEndPeriod.name,
          sourceEndYear.name
        ).then(response => {
          getStartAndEndPeriods(response.data);
        });
      } else if (sourceStartPeriod && sourceStartYear && destinationStartPeriod && destinationStartYear && sourceEndPeriod) {
        getPeriodInfo(
          copyVersionService.planID,
          sourceStartPeriod.name,
          sourceStartYear.name,
          destinationStartPeriod.name,
          destinationStartYear.name,
          sourceEndPeriod.name
        ).then(response => {
          getStartAndEndPeriods(response.data);
        });
      }
    } else {
      if (sourceStartPeriod && sourceStartYear && destinationStartPeriod && destinationStartYear && sourceEndPeriod && sourceEndYear.name) {
        getPeriodInfo(
          copyVersionService.planID,
          sourceStartPeriod.name,
          sourceStartYear.name,
          destinationStartPeriod.name,
          destinationStartYear.name,
          sourceEndPeriod.name,
          sourceEndYear.name,
          sourceBeginningBalancePeriod ? sourceBeginningBalancePeriod.name : null,
          sourceBeginningBalanceYear ? sourceBeginningBalanceYear.name : null
        ).then(response => {
          getStartAndEndPeriods(response.data);
        });
      } else if (sourceStartPeriod && sourceStartYear && destinationStartPeriod && destinationStartYear && sourceEndPeriod && !sourceEndYear.name) {
        getPeriodInfo(
          copyVersionService.planID,
          sourceStartPeriod.name,
          sourceStartYear.name,
          destinationStartPeriod.name,
          destinationStartYear.name,
          sourceEndPeriod.name,
          null,
          sourceBeginningBalancePeriod ? sourceBeginningBalancePeriod.name : null,
          sourceBeginningBalanceYear ? sourceBeginningBalanceYear.name : null
        ).then(response => {
          getStartAndEndPeriods(response.data);
        });
      } else if (sourceStartPeriod && sourceStartYear && destinationStartPeriod && destinationStartYear && sourceEndPeriod) {
        getPeriodInfo(
          copyVersionService.planID,
          sourceStartPeriod.name,
          sourceStartYear.name,
          destinationStartPeriod.name,
          destinationStartYear.name,
          sourceEndPeriod.name
        ).then(response => {
          getStartAndEndPeriods(response.data);
        });
      }
    }
  };

  const updateSourceAndDestinationStartEndYears = () => {
    if (timeDimensionInfo) {
      let timeFiscalStartYear = parseInt(timeDimensionInfo.firstFiscalYear);
      let timeFiscalEndYear = parseInt(timeDimensionInfo.firstFiscalYear) + parseInt(timeDimensionInfo.numOfYears) - 1;

      let tempSourceStartYears = [];
      let tempSourceEndYears = [];
      let tempDestinationStartYears = [];
      let tempSourceBeginningBalanceYears = [];

      for (let t = timeFiscalStartYear; t <= timeFiscalEndYear; t++) {
        tempSourceStartYears.push({ id: t, name: t });
        tempSourceEndYears.push({ id: t, name: t });
        tempDestinationStartYears.push({ id: t, name: t });
        tempSourceBeginningBalanceYears.push({ id: t, name: t });
      }

      setSourceStartYears(tempSourceStartYears);
      setSourceEndYears(tempSourceEndYears);
      setDestinationStartYears(tempDestinationStartYears);
      setSourceBeginningBalanceYears(tempSourceBeginningBalanceYears);
    }
  };

  const calculatePeriods = () => {
    setSourceStartPeriods([]);
    setDestinationStartPeriods([]);
    setSourceEndPeriods([]);
    setSourceBeginningBalancePeriods([]);

    let tempSourceStartPeriods = [];
    let tempDestinationStartPeriods = [];
    let tempSourceEndPeriods = [];
    let tempSourceBeginningBalancePeriods = [];

    for (let key in periodMap) {
      if (periodMap[key] !== 'NE') {
        if (copyOption === 'PD') {
          if (periodMap[key] !== '0' && periodMap[key] !== '999') {
            tempSourceStartPeriods.push({ id: periodMap[key], name: key });
            tempDestinationStartPeriods.push({ id: periodMap[key], name: key });
            tempSourceBeginningBalancePeriods.push({ id: periodMap[key], name: key });
          } else {
            if (copyOption === 'BB') {
              tempSourceBeginningBalancePeriods.push({ id: periodMap[key], name: key });
            }
          }
        } else {
          if (periodMap[key] === '0') {
            tempDestinationStartPeriods.push({ id: periodMap[key], name: key });
            tempSourceBeginningBalancePeriods.push({ id: periodMap[key], name: key });
          } else {
            if (copyOption === 'BB') {
              tempSourceBeginningBalancePeriods.push({ id: periodMap[key], name: key });
            }
            if (periodMap[key] !== '999') {
              tempSourceStartPeriods.push({ id: periodMap[key], name: key });
            }
          }
        }
      }

      if (periodMap[key] !== '0' && periodMap[key] !== '999') {
        if (periodMap[key] === 'NE') {
          tempSourceEndPeriods.push({ id: periodMap[key], name: NEVER_ENDS });
        } else {
          tempSourceEndPeriods.push({ id: periodMap[key], name: key });
        }
      }
    }
    setSourceStartPeriods(tempSourceStartPeriods);
    setDestinationStartPeriods(tempDestinationStartPeriods);
    setSourceEndPeriods(tempSourceEndPeriods);
    setSourceBeginningBalancePeriods(tempSourceBeginningBalancePeriods);
  };

  const calculateYear = () => {
    setSourceStartYear({ id: new Date().getFullYear() - 1, name: new Date().getFullYear() - 1 });
    let year = sourceStartYears.find(el => el.name === sourceStartYear.name);
    if (!year) {
      setSourceStartYear(sourceStartYears[0]);
    }

    setSourceBeginningBalanceYear({ id: new Date().getFullYear() - 1, name: new Date().getFullYear() - 1 });
    year = sourceBeginningBalanceYears.find(el => el.name === sourceStartYear.name);
    if (!year) {
      setSourceBeginningBalanceYear(sourceBeginningBalanceYears[0]);
    }

    setDestinationStartYear({ id: new Date().getFullYear(), name: new Date().getFullYear() });
    let destYear = destinationStartYears.find(el => el.name === destinationStartYear.name);
    if (!destYear) {
      setDestinationStartYear(destinationStartYears[0]);
    }
  };

  const calculateYearAndPeriodInfo = initialize => {
    if (initialize) {
      if (!copyVersionService.datesSelected) {
        calculateYear();
      } else {
        updateSourceAndDestinationStartEndYears();

        setSourceStartYear({ id: copyVersionService.sourceStartYear, name: copyVersionService.sourceStartYear });
        setSourceEndYear({ id: copyVersionService.sourceEndYear, name: copyVersionService.sourceEndYear });
        setSourceBeginningBalanceYear({
          id: copyVersionService.sourceBeginningBalanceYear,
          name: copyVersionService.sourceBeginningBalanceYear,
        });
        setSourceBeginningBalancePeriod({
          id: periodMap[copyVersionService.sourceBeginningBalancePeriod],
          name: copyVersionService.sourceBeginningBalancePeriod,
        });

        setSourceStartPeriod({
          id: periodMap[copyVersionService.sourceStartPeriod],
          name: copyVersionService.sourceStartPeriod,
        });
        if (copyVersionService.sourceEndPeriod === NEVER_ENDS) {
          setSourceEndPeriod({ id: periodMap['NE'], name: copyVersionService.sourceEndPeriod });
        } else {
          setSourceEndPeriod({
            id: periodMap[copyVersionService.sourceEndPeriod],
            name: copyVersionService.sourceEndPeriod,
          });
        }

        if ((copyOption === 'BB' && isBBDestinationDataApplied) || (copyOption === 'PD' && isPDDestinationDataApplied)) {
          setDestinationStartPeriod({
            id: periodMap[copyVersionService.destinationStartPeriod],
            name: copyVersionService.destinationStartPeriod,
          });
          setDestinationStartYear({
            id: copyVersionService.destinationStartYear,
            name: copyVersionService.destinationStartYear,
          });
        }
      }
    } else {
      updateSourceAndDestinationStartEndYears();
      calculateYear();
    }
  };

  const getInitialPeriods = () => {
    setSourceStartPeriod({ id: periodMap['P01'], name: 'P01' });
    setSourceEndPeriod({ id: periodMap['NE'], name: NEVER_ENDS });
    setSourceBeginningBalancePeriod({ id: periodMap['BB'], name: 'BB' });
    if (copyOption === 'PD') {
      setDestinationStartPeriod({ id: periodMap['P01'], name: 'P01' });
    } else {
      setDestinationStartPeriod({ id: periodMap['BB'], name: 'BB' });
    }
  };

  const getStartAndEndPeriods = data => {
    if (data.sourceStartStartDate) {
      setSourceStartDate(data.sourceStartStartDate + ' - ' + data.sourceStartEndDate);
    } else {
      if (copyOption === 'BB') {
        if (sourceStartPeriod && sourceStartPeriod.name === 'BB') {
          setSourceStartDate(BEGINNING_BALANCE);
        } else if (sourceStartPeriod && sourceStartPeriod.name === 'C') {
          setSourceStartDate(CLOSING_ENTRY);
        }
      }
    }

    if (data.sourceEndStartDate) {
      setSourceEndDate(data.sourceEndStartDate + ' - ' + data.sourceEndEndDate);
    } else {
      if (sourceEndPeriod && sourceEndPeriod.name === NEVER_ENDS) {
        setSourceEndDate(NEVER_ENDS);
      } else if (sourceEndPeriod && sourceEndPeriod.name === 'BB' && copyOption === 'BB') {
        setSourceEndDate(BEGINNING_BALANCE);
      } else if (sourceEndPeriod && sourceEndPeriod.name === 'C' && copyOption === 'BB') {
        setSourceEndDate(CLOSING_ENTRY);
      }
    }

    if (data.destStartStartDate) {
      setDestStartDate(data.destStartStartDate + ' - ' + data.destStartEndDate);
    } else {
      if (copyOption === 'BB') {
        if (destinationStartPeriod && destinationStartPeriod.name === 'BB') {
          setDestStartDate(BEGINNING_BALANCE);
        } else if (destinationStartPeriod && destinationStartPeriod.name === 'C') {
          setDestStartDate(CLOSING_ENTRY);
        }
      }
    }

    if (data.sourceBeginningBalanceStartDate) {
      setSourceBeginningBalanceDate(data.sourceBeginningBalanceStartDate + ' - ' + data.sourceBeginningBalanceEndDate);
    } else {
      if (copyOption === 'BB') {
        if (sourceBeginningBalancePeriod && sourceBeginningBalancePeriod.name === 'BB') {
          setSourceBeginningBalanceDate(BEGINNING_BALANCE);
        } else if (sourceBeginningBalancePeriod && sourceBeginningBalancePeriod.name === 'C') {
          setSourceBeginningBalanceDate(CLOSING_ENTRY);
        }
      }
    }
  };

  const initSourceBeginningBalance = () => {
    setSourceStartPeriods([]);
    setDestinationStartPeriods([]);
    setSourceEndPeriods([]);
    setSourceBeginningBalancePeriods([]);

    //calculatePeriods();
    setSourceBeginningBalancePeriod({ id: periodMap['P12'], name: 'P12' });
    setSourceBeginningBalanceYear({ id: new Date().getFullYear() - 1, name: new Date().getFullYear() - 1 });
    let year = sourceBeginningBalanceYears.find(el => el.name === sourceStartYear.name);
    if (!year) {
      setSourceBeginningBalanceYear(sourceBeginningBalanceYears[0]);
    }
  };

  const applyDates = () => {
    copyVersionService.applyDates(
      copyOption === 'BB',
      false,
      sourceStartPeriod ? sourceStartPeriod.name : null,
      sourceStartYear ? sourceStartYear.name : null,
      destinationStartPeriod ? destinationStartPeriod.name : null,
      destinationStartYear ? destinationStartYear.name : null,
      sourceEndPeriod ? sourceEndPeriod.name : null,
      sourceEndYear ? sourceEndYear.name : null,
      sourceBeginningBalancePeriod ? sourceBeginningBalancePeriod.name : null,
      sourceBeginningBalanceYear ? sourceBeginningBalanceYear.name : null,
      copyOption === 'PD'
    );
    if (copyOption === 'PD') {
      setIsPDDestinationDataApplied(true);
      setIsBBDestinationDataApplied(false);
    } else {
      setIsPDDestinationDataApplied(false);
      setIsBBDestinationDataApplied(true);
    }
    setIsDatesSelected(true);
    closePeriodsDialog(true);
  };

  const initPeriods = () => {
    setSourceStartPeriods([]);
    setDestinationStartPeriods([]);
    setSourceEndPeriods([]);
    setSourceBeginningBalancePeriods([]);

    calculatePeriods();

    if (copyOption === 'PD') {
      setDestinationStartPeriod({ id: periodMap['P01'], name: 'P01' });
    } else {
      setDestinationStartPeriod({ id: periodMap['BB'], name: 'BB' });
    }
  };

  const handleChangeSourceBeginningBalancePeriod = event => {
    setSourceBeginningBalancePeriod({ id: periodMap[event.target.value], name: event.target.value });
  };

  const handleChangeSourceBeginningBalanceYear = event => {
    setSourceBeginningBalanceYear({ id: event.target.value, name: event.target.value });
  };

  const handleChangeDestinationStartPeriod = event => {
    setDestinationStartPeriod({ id: periodMap[event.target.value], name: event.target.value });
  };

  const handleChangeDestinationStartYear = event => {
    setDestinationStartYear({ id: event.target.value, name: event.target.value });
  };

  const handleChangeSourceStartPeriod = event => {
    setSourceStartPeriod({ id: periodMap[event.target.value], name: event.target.value });
  };

  const handleChangeSourceStartYear = event => {
    setSourceStartYear({ id: event.target.value, name: event.target.value });
  };

  const handleChangeSourceEndPeriod = event => {
    setSourceEndPeriod({ id: periodMap[event.target.value], name: event.target.value });
  };

  const handleChangeSourceEndYear = event => {
    setSourceEndYear({ id: event.target.value, name: event.target.value });
  };

  const closeDialog = isDatesSelected => {
    setShowDialog(false);
    if (!isDatesSelected) {
      setCopyOption('');
    }
  };

  const closePeriodsDialog = isDatesSelected => {
    setShowBBDialog(false);
    setShowPDDialog(false);
    if (!isDatesSelected) {
      setCopyOption('');
    }
  };

  const proceedToNextCopyDataModal = copyOption => {
    setShowDialog(false);
    copyOption === 'BB' ? setShowBBDialog(true) : setShowPDDialog(true);
  };

  const clearBeginningBalanceSelections = () => {
    setSourceBeginningBalanceYears([]);
    setIsBBDestinationDataApplied(false);
  };

  const clearManualPeriodsSelections = () => {
    setSourceEndYear({ id: '', name: '' });
    setSourceStartPeriods([]);
    setDestinationStartPeriods([]);
    setSourceEndPeriods([]);
    setSourceStartYears([]);
    setSourceEndYears([]);
    setDestinationStartYears([]);
    setIsPDDestinationDataApplied(false);
  };

  const handleRadioButtonChange = event => {
    if (isDatesSelected) {
      setIsDatesSelected(false);
      copyVersionService.datesSelected = false;
      copyVersionService.resize();
      if (copyOption === 'BB') {
        clearBeginningBalanceSelections();
      } else {
        clearManualPeriodsSelections();
      }
      setSuccessMessage('DATES_DISABLED');
      setShowSuccessMessage(true);
      calculatePeriods();
      getInitialPeriods();
      calculateYearAndPeriodInfo(false);
    }
    setCopyOption(event.target.value);
  };

  const handleClick = () => {
    setShowDialog(true);
  };

  const workFLowPopupTitleContent = () => {
    return (
      <div className="CVDialogTitleWrapper">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('QUESTION')}</Box>
        </Typography>
      </div>
    );
  };

  const workFlowPopupContent = () => {
    return (
      <WorkflowPopup
        tc={tc}
        copyOption={copyOption}
        closeDialog={() => closeDialog(isDatesSelected)}
        proceedToNextCopyDataModal={proceedToNextCopyDataModal}
        handleRadioButtonChange={handleRadioButtonChange}
      />
    );
  };

  const bbPopupTitleContent = () => {
    return (
      <div className="CVDialogTitleWrapper">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('SELECT_PERIODS_BB')}</Box>
        </Typography>
        <TooltipComponent t={t} i18nKey={tc('COPY_DATA_SELECT_PERIODS_COPY_BB')}></TooltipComponent>
      </div>
    );
  };

  const bbPopupContent = () => {
    return (
      <Fragment>
        <div className="CVSelectTableRowWrapper">
          <CopyVersionDropdownGroup
            dropdownGroupHeader={tc('SOURCE')}
            tc={tc}
            periodLabelId="sourceBBPeriodId"
            yearLabelId="sourceBBYearId"
            periodOnChange={handleChangeSourceBeginningBalancePeriod}
            yearOnChange={handleChangeSourceBeginningBalanceYear}
            periodValue={!!sourceBeginningBalancePeriod ? sourceBeginningBalancePeriod.name : ''}
            yearValue={!!sourceBeginningBalanceYear ? sourceBeginningBalanceYear.name : ''}
            periodOptions={sourceBeginningBalancePeriods}
            yearOptions={sourceBeginningBalanceYears}
            dateRange={sourceBeginningBalanceDate}
          />
          <CopyVersionDropdownGroup
            dropdownGroupHeader={tc('DESTINATION')}
            tc={tc}
            periodLabelId="destinationBBPeriodId"
            yearLabelId="destinationBBYearId"
            periodOnChange={handleChangeDestinationStartPeriod}
            yearOnChange={handleChangeDestinationStartYear}
            periodValue={destinationStartPeriod !== null ? destinationStartPeriod.name : ''}
            yearValue={destinationStartYear !== null ? destinationStartYear.name : ''}
            periodOptions={destinationStartPeriods}
            yearOptions={destinationStartYears}
            dateRange={destStartDate}
          />
        </div>
        <FooterButtonGroup
          tc={tc}
          actionButtonId="applyButton"
          actionButtonLabel="APPLY"
          actionButtonDisabled={false}
          actionButtonType="submit"
          actionButtonOnClick={applyDates}
          cancelButtonOnClick={() => closePeriodsDialog(isDatesSelected)}
        />
      </Fragment>
    );
  };

  const periodsPopupTitleContent = () => {
    return (
      <div className="CVDialogTitleWrapper">
        <Typography variant="h4">
          <Box fontWeight="bold">{tc('SELECT_PERIODS_PD')}</Box>
        </Typography>
        <TooltipComponent t={t} i18nKey={tc('COPY_DATA_SELECT_PERIODS_COPY_ACTIVITY')}></TooltipComponent>
      </div>
    );
  };

  const periodsPopupContent = () => {
    return (
      <Fragment>
        {/*<div>SOURCE HEADER</div>*/}
        <Typography variant="h5" className="CVSelectHeader">
          <Box fontWeight="bold">{tc('SOURCE')}</Box>
        </Typography>
        <div className="CVSelectTableRowWrapper">
          <CopyVersionDropdownGroup
            dropdownGroupHeader={tc('START_DATE')}
            tc={tc}
            periodLabelId="sourceStartPeriodId"
            yearLabelId="sourceStartYearId"
            periodOnChange={handleChangeSourceStartPeriod}
            yearOnChange={handleChangeSourceStartYear}
            periodValue={!!sourceStartPeriod ? sourceStartPeriod.name : ''}
            yearValue={!!sourceStartYear ? sourceStartYear.name : ''}
            periodOptions={sourceStartPeriods}
            yearOptions={sourceStartYears}
            dateRange={sourceStartDate}
          />
          <CopyVersionDropdownGroup
            dropdownGroupHeader={tc('END_DATE')}
            tc={tc}
            periodLabelId="sourceEndPeriodId"
            yearLabelId="sourceEndYearId"
            periodOnChange={handleChangeSourceEndPeriod}
            yearOnChange={handleChangeSourceEndYear}
            periodValue={!!sourceEndPeriod ? sourceEndPeriod.name : ''}
            yearValue={!!sourceEndYear ? sourceEndYear.name : ''}
            periodOptions={sourceEndPeriods}
            yearOptions={sourceEndYears}
            dateRange={sourceEndDate}
            disabled={sourceEndYearDisabled}
          />
        </div>

        {/*<div>DESTINATION HEADER</div>*/}
        <Typography variant="h5" className="CVSelectHeader">
          <Box fontWeight="bold">{tc('DESTINATION')}</Box>
        </Typography>

        <div className="CVSelectTableRowWrapper">
          <CopyVersionDropdownGroup
            groupStyle={{ columnGap: '10%' }}
            dropdownStyle={{ flex: 1 }}
            dropdownGroupHeader={tc('BEGINNING_WITH')}
            tc={tc}
            periodLabelId="destinationStartPeriodId"
            yearLabelId="destinationStartYearId"
            periodOnChange={handleChangeDestinationStartPeriod}
            yearOnChange={handleChangeDestinationStartYear}
            periodValue={!!destinationStartPeriod ? destinationStartPeriod.name : ''}
            yearValue={!!destinationStartYear ? destinationStartYear.name : ''}
            periodOptions={destinationStartPeriods}
            yearOptions={destinationStartYears}
            dateRange={destStartDate}
          />
        </div>
        <FooterButtonGroup
          tc={tc}
          actionButtonId="applyButton"
          actionButtonLabel="APPLY"
          actionButtonDisabled={false}
          actionButtonType="submit"
          actionButtonOnClick={applyDates}
          cancelButtonOnClick={() => closePeriodsDialog(isDatesSelected)}
        />
      </Fragment>
    );
  };

  return (
    <Fragment>
      <button
        id="datesButton"
        className={`select-profile btn btn-primary btn-outline dates ladda-button ${copyVersionService.datesSelected ? 'active' : ''}`}
        disabled={false}
        onClick={handleClick}>
        <span className="CVSelectPeriodButton">{tc('DATES')}</span>
      </button>

      <MuiDialog
        open={!!showDialog ? showDialog : false}
        onClose={() => closeDialog(isDatesSelected)}
        dialogId="choose-copy-option-title"
        dialogTitleContent={workFLowPopupTitleContent}
        dialogContent={workFlowPopupContent}
      />

      <MuiDialog
        open={!!showBBDialog ? showBBDialog : false}
        onClose={() => closePeriodsDialog(isDatesSelected)}
        dialogId="select-bb-periods-title"
        dialogTitleContent={bbPopupTitleContent}
        dialogContent={bbPopupContent}
      />

      <MuiDialog
        open={!!showPDDialog ? showPDDialog : false}
        onClose={() => closePeriodsDialog(isDatesSelected)}
        dialogId="select-periods-title"
        dialogTitleContent={periodsPopupTitleContent}
        dialogContent={periodsPopupContent}
      />
      <SuccessMessage open={showSuccessMessage} setOpen={setShowSuccessMessage} message={tc(successMessage)} type="success" />
    </Fragment>
  );
};